import { Container, Graphics, type Sprite } from 'pixi.js';
import { FACE_RADIUS, INK, MARK, MASK_BODY, maskTone } from '../face-kit';
import type { Frame } from '../frame';
import { type Ctx, paint, type Shape } from '../paint';
import { add, setTint } from '../util';
import type { Point } from './cable';
import { type Cadence, WAVE, conePump, wave, wavesBorn, windowOpen } from './larsen-moments';

// The floor monitor the Larsen hears himself in. Its own frame: +x faces him, the slanted face
// runs from the ridge (x -12) down to the floor (x 24), in units of WEDGE.scale face units.
// It stands `inset` in from where the Larsen came in, so the edge of the scene does not cut it.
const WEDGE = { scale: 1.23, width: 56, height: 76, inset: 38 };
const DENSITY = 3;
const OUTLINE: readonly (readonly [number, number])[] = [
  [-24, -24],
  [-12, -25],
  [24, -33],
  [24, 33],
  [-12, 25],
  [-24, 24],
];
const FACE: readonly (readonly [number, number])[] = [
  [-12, -25],
  [24, -33],
  [24, 33],
  [-12, 25],
];
const HORN = {
  mouth: [
    [1, 16],
    [17, 14],
    [17, 29],
    [1, 25],
  ],
  throat: [
    [7, 19.5],
    [11, 19.2],
    [11, 22.6],
    [7, 22],
  ],
} as const;
const CONE = { x: 7, y: -5, rx: 13.5, ry: 19, pump: 0.16, shift: 1.2 };
const SOCKET = { x: -16, y: 30.5, light: 2.2 };
const ARC = { from: 8, steps: 48, span: 1.05, narrow: 0.35, alpha: 1.25 };
const GLOW = { reach: 30, rings: 3, gold: 0.55, rim: 0.25 };
const SOFT = 'rgb(255 255 255 / 0.35)';
const FAINT = 'rgb(255 255 255 / 0.25)';

function trace(ctx: Ctx, points: readonly (readonly [number, number])[]): void {
  ctx.beginPath();
  for (const [x, y] of points) {
    ctx.lineTo(x, y);
  }
  ctx.closePath();
}

// Grays and white, tinted with the palette of the hour like the masks.
export function paintWedge(): Shape {
  return paint(
    WEDGE.width,
    WEDGE.height,
    WEDGE.height / 2,
    (ctx) => {
      ctx.lineJoin = 'round';
      trace(ctx, OUTLINE);
      ctx.fillStyle = MASK_BODY;
      ctx.fill();
      ctx.lineWidth = 4;
      ctx.strokeStyle = MARK;
      ctx.stroke();
      trace(ctx, FACE);
      const slope = ctx.createLinearGradient(-12, 0, 24, 0);
      slope.addColorStop(0, '#343434');
      slope.addColorStop(1, '#1d1d1d');
      ctx.fillStyle = slope;
      ctx.fill();
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(-12, -23);
      ctx.lineTo(-12, 23);
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = SOFT;
      ctx.stroke();
      ctx.beginPath();
      ctx.roundRect(-20.5, -8, 5, 16, 2.5);
      ctx.fillStyle = INK;
      ctx.fill();
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = FAINT;
      ctx.stroke();
      trace(ctx, HORN.mouth);
      ctx.fillStyle = INK;
      ctx.fill();
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = 'rgb(255 255 255 / 0.55)';
      ctx.stroke();
      ctx.beginPath();
      HORN.mouth.forEach(([x, y], index) => {
        const [tx, ty] = HORN.throat[index] ?? [x, y];
        ctx.moveTo(x, y);
        ctx.lineTo(tx, ty);
      });
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgb(255 255 255 / 0.22)';
      ctx.stroke();
      trace(ctx, HORN.throat);
      ctx.fillStyle = '#000000';
      ctx.fill();
      ctx.strokeStyle = 'rgb(255 255 255 / 0.4)';
      ctx.stroke();
      ctx.beginPath();
      ctx.ellipse(CONE.x, CONE.y, CONE.rx, CONE.ry, 0, 0, Math.PI * 2);
      ctx.fillStyle = INK;
      ctx.fill();
      ctx.lineWidth = 2.4;
      ctx.strokeStyle = MARK;
      ctx.stroke();
      ctx.beginPath();
      ctx.roundRect(-21, 26, 10, 9, 2.5);
      ctx.fillStyle = INK;
      ctx.fill();
      ctx.strokeStyle = MARK;
      ctx.stroke();
    },
    DENSITY,
  );
}

export interface Wedge {
  // Where the cable plugs in, in the scene.
  readonly socket: Point;
  place(at: Point, facing: number, radius: number, alpha: number): void;
  // Its cone pumps with each cry of the Larsen and its waves fly back at him, unless still.
  play(beats: number, cadence: Cadence, frame: Frame, still: boolean): void;
  destroy(): void;
}

export function createWedge(parent: Container, look: Shape): Wedge {
  const root = parent.addChild(new Container());
  const waves = root.addChild(new Graphics());
  const body: Sprite = add(root, look);
  const cone = root.addChild(new Graphics());
  const socket = { x: 0, y: 0 };
  let unit = 1;

  function cry(born: number, beats: number, cadence: Cadence, frame: Frame): void {
    const ring = wave(born, beats, false, windowOpen(born, cadence));
    if (ring === undefined) {
      return;
    }
    const p = (ring.radius - WAVE.from) / (WAVE.to - WAVE.from);
    const toLocal = FACE_RADIUS / WEDGE.scale;
    const [reach, bristle] = [ring.radius * toLocal, ring.bristle * toLocal];
    const span = ARC.span - ARC.narrow * p;
    for (let step = 0; step <= ARC.steps; step += 1) {
      const angle = -span + (2 * span * step) / ARC.steps;
      const r = reach + bristle * Math.sin(angle * WAVE.lobes + ring.turn);
      const x = ARC.from + Math.cos(angle) * r;
      const y = Math.sin(angle) * r;
      if (step === 0) {
        waves.moveTo(x, y);
      } else {
        waves.lineTo(x, y);
      }
    }
    waves.stroke({
      width: (ring.gold ? WAVE.goldWidth : WAVE.width) / unit,
      color: ring.gold ? frame.palette.noyau : frame.palette.badVibeRim,
      alpha: Math.min(1, ring.alpha * ARC.alpha),
      cap: 'round',
    });
  }

  return {
    socket,
    place(at, facing, radius, alpha): void {
      unit = (WEDGE.scale * radius) / FACE_RADIUS;
      const [cos, sin] = [Math.cos(facing), Math.sin(facing)];
      const local = (x: number, y: number) =>
        [at.x + unit * (x * cos - y * sin), at.y + unit * (x * sin + y * cos)] as const;
      root.position.set(...local(WEDGE.inset, 0));
      root.rotation = facing;
      root.scale.set(unit);
      root.alpha = alpha;
      root.visible = alpha > 0;
      [socket.x, socket.y] = local(WEDGE.inset + SOCKET.x, SOCKET.y);
    },
    play(beats, cadence, frame, still): void {
      const { palette } = frame;
      waves.clear();
      cone.clear();
      setTint(body, palette.badVibeRim);
      const kick = conePump(beats, still);
      const gold = windowOpen(beats, cadence);
      if (!still) {
        for (const born of wavesBorn(beats)) {
          cry(born, beats, cadence, frame);
        }
      }
      const pump = 1 + CONE.pump * kick;
      const shine = (gold ? GLOW.gold : GLOW.rim) * kick;
      if (shine > 0) {
        for (let ring = GLOW.rings; ring >= 1; ring -= 1) {
          cone.circle(CONE.x, CONE.y, (GLOW.reach * pump * ring) / GLOW.rings).fill({
            color: gold ? palette.or : palette.badVibeRim,
            alpha: shine / GLOW.rings,
          });
        }
      }
      const rim = palette.badVibeRim;
      cone
        .ellipse(CONE.x, CONE.y, 11 * pump, 15.5 * pump)
        .fill({ color: maskTone(rim, '#2a2a2a') })
        .stroke({ width: 1.4, color: rim, alpha: 0.4 })
        .ellipse(CONE.x, CONE.y, 7 * pump, 10 * pump)
        .stroke({ width: 1.2, color: rim, alpha: 0.18 })
        .ellipse(CONE.x + CONE.shift * kick, CONE.y, 4.2 * pump, 6 * pump)
        .fill({ color: gold ? palette.noyau : maskTone(rim, '#4a4a4a') })
        .stroke({ width: 1.6, color: rim })
        .circle(SOCKET.x, SOCKET.y, SOCKET.light)
        .fill({ color: gold ? palette.or : rim });
    },
    destroy(): void {
      root.destroy({ children: true });
    },
  };
}
